<script setup>

import {computed, ref} from "vue";
import { trans } from 'laravel-vue-i18n';
import calPreviewDark from '~@/images/cal-preview-dark.png';
import calPreviewLight from '~@/images/cal-preview-light.png';
import UiCalendar from "../../../../widget/components/ui-components/UiCalendar.vue";
import SelectButton from "primevue/selectbutton";
import Slider from "primevue/slider";
import Button from "primevue/button";
import Select from "primevue/select";
import ToggleButton from "primevue/togglebutton";
import ButtonGroup from "primevue/buttongroup";

const previewStyle = computed(() => {
    return {
        backgroundImage: customerInfo.value.theme.darkMode ? `url(${calPreviewDark})` : `url(${calPreviewLight})`,
    }
})

const emit = defineEmits(["done"]);

const customerInfo = ref({
    logo: 'http://localhost:8080/img/logo.661f1b58.svg',
    calendar: {
        firstDayOfWeek: 2,
        titlePosition: 'center',
        transition: 'fade',
        locale: 'cs_CZ',
        showWeekNumbers: false,
        view: 'monthly',
    },
    theme: {
        color: 'blue',
        borderless: false,
        transparent: false,
        darkMode: false,
        darkModeLock: false,
        fontFamily: 'Syne',
        rows: 1,
        columns: 1,
        roundness: 1
    }
});

const availableColors = [
    'gray',
    'red',
    'orange',
    'yellow',
    'green',
    'teal',
    'blue',
    'indigo',
    'purple',
    'pink',
];

const borderLess = [
    {
        value: false,
        label: trans('forms.prefs_frame'),
        icon: 'pi pi-stop'
    },
    {
        value: true,
        label: trans('forms.prefs_borderless'),
        icon: 'pi pi-ban'
    }
];

const transparent = [
    {
        value: false,
        label: trans('forms.prefs_bg'),
        icon: 'pi pi-circle-fill'
    },
    {
        value: true,
        label: trans('forms.prefs_transparent'),
        icon: 'pi pi-circle'
    }
];

const columns = [
    {
        value: 1,
        icon: 'pi pi-calendar',
        label: trans('forms.prefs_one')
    },
    {
        value: 2,
        icon: 'pi pi-calendar-plus',
        label: trans('forms.prefs_two')
    }
];

const days = [
    {
        label: trans('common.dow_long.mon'),
        value: 2
    },
    {
        label: trans('common.dow_long.tue'),
        value: 3
    },
    {
        label: trans('common.dow_long.wed'),
        value: 4
    },
    {
        label: trans('common.dow_long.thu'),
        value: 5
    },
    {
        label: trans('common.dow_long.fri'),
        value: 6
    },
    {
        label: trans('common.dow_long.sat'),
        value: 7
    },
    {
        label: trans('common.dow_long.sun'),
        value: 1
    },

];

const selectedDate = ref(new Date())

const titlePosition = [
    {
        value: 'left',
        icon: 'pi pi-align-left',
        label: trans('forms.theme_position_left')
    },
    {
        value: 'center',
        icon: 'pi pi-align-center',
        label: trans('forms.theme_position_center')
    }
];

const calendarView = [
    {
        value: 'monthly',
        icon: 'pi pi-th-large',
        label: trans('forms.prefs_monthly')
    },
    {
        value: 'weekly',
        icon: 'pi pi-stop',
        label: trans('forms.prefs_weekly')
    }
];
</script>

<template>
    <div>
        <div class="border dark:border-gray-800 rounded-lg overflow-hidden my-n2 flex flex-col">
            <div class="border-b dark:border-gray-800 p-4 bg-center bg-cover flex justify-center relative" :style="previewStyle">
                <div class="absolute top-4 right-4">
                    <ButtonGroup>
                        <Button rounded :disabled="customerInfo.theme.darkModeLock" :severity="customerInfo.theme.darkMode ? 'contrast' : 'secondary'" :icon="customerInfo.theme.darkMode ? 'pi pi-moon' : 'pi pi-sun'" :label="customerInfo.theme.darkMode ? $t('forms.theme_mode_dark') : $t('forms.theme_mode_light')" @click="customerInfo.theme.darkMode = !customerInfo.theme.darkMode"></Button>
                        <Button rounded :severity="customerInfo.theme.darkMode ? 'secondary' : 'contrast'" :icon="customerInfo.theme.darkModeLock ? 'pi pi-lock' : 'pi pi-lock-open'" @click="customerInfo.theme.darkModeLock = !customerInfo.theme.darkModeLock"></Button>
                    </ButtonGroup>
                </div>
                <ui-calendar :customer-info="customerInfo" mode="date" :key="`${customerInfo.calendar.titlePosition}-${customerInfo.calendar.showWeekNumbers}`" :events="[]" :date-time-restrictions="{}" v-model="selectedDate"></ui-calendar>
            </div>
            <div class="p-5 flex flex-wrap justify-around gap-5">
                <div class="flex flex-col gap-2 items-center">
                    <label class="text-sm" for="theme.color">
                        {{ $t('forms.prefs_accent_color') }}
                    </label>
                    <div class="grid grid-cols-5 gap-y-2 gap-x-1 justify-center">
                        <div v-for="color in availableColors"
                             class="border dark:border-gray-800 rounded cursor-pointer opacity-90 hover:opacity-70 transition-all flex items-center justify-center text-white"
                             @click="customerInfo.theme.color = color"
                             style="width: 30px; height: 30px"
                             :class="`bg-${color}-500 ${customerInfo.theme.color === color ? 'opacity-100 border-2 shadow':''}`">
                            {{selectedDate.getDate()}}
                        </div>
                    </div>
                </div>
                <div class="border-l dark:border-gray-800"></div>
                <div class="flex flex-col gap-2 items-center">
                    <label class="text-sm" for="theme.color">
                        {{ $t('forms.theme_calendar_bg') }}
                    </label>
                    <SelectButton v-model="customerInfo.theme.borderless" :options="borderLess" optionLabel="value" optionValue="value" dataKey="value" aria-labelledby="custom">
                        <template #option="slotProps">
                            <i :class="slotProps.option.icon"></i> {{slotProps.option.label}}
                        </template>
                    </SelectButton>
                    <SelectButton v-model="customerInfo.theme.transparent" size="small" :options="transparent" optionLabel="value" optionValue="value" dataKey="value" aria-labelledby="custom">
                        <template #option="slotProps">
                            <i :class="slotProps.option.icon"></i> {{slotProps.option.label}}
                        </template>
                    </SelectButton>
                </div>
                <div class="border-l dark:border-gray-800"></div>
                <div class="flex flex-col gap-2 items-center">
                    <label class="text-sm" for="theme.color">
                        {{ $t('forms.prefs_roundness') }}
                    </label>
                    <Slider v-model="customerInfo.theme.roundness" :min="0" :max="3" class="w-56" />
                    <label class="text-sm mt-3" for="theme.color">
                        {{ $t('forms.prefs_visible_months') }}
                    </label>
                    <SelectButton v-model="customerInfo.theme.columns" size="small" :options="columns" optionLabel="value" optionValue="value" dataKey="value" aria-labelledby="custom">
                        <template #option="slotProps">
                            <i :class="slotProps.option.icon"></i> {{slotProps.option.label}}
                        </template>
                    </SelectButton>
                </div>
            </div>
            <div class="m-5 mt-0 pt-5 flex flex-wrap justify-around gap-5 border-t dark:border-gray-800">
                <div class="flex flex-col gap-2 items-center">
                    <label class="text-sm" for="theme.color">
                        {{ $t('forms.theme_first_day') }}
                    </label>
                    <Select v-model="customerInfo.calendar.firstDayOfWeek" class="w-56" :options="days" optionLabel="label" optionValue="value"></Select>
                </div>
                <div class="border-l dark:border-gray-800"></div>
                <div class="flex flex-col gap-2 items-center">
                    <label class="text-sm" for="theme.color">
                        {{ $t('forms.theme_month_position') }}
                    </label>
                    <SelectButton v-model="customerInfo.calendar.titlePosition" :options="titlePosition" optionLabel="value" optionValue="value" dataKey="value" aria-labelledby="custom">
                        <template #option="slotProps">
                            <i :class="slotProps.option.icon"></i> {{slotProps.option.label}}
                        </template>
                    </SelectButton>
                </div>
                <div class="border-l dark:border-gray-800"></div>
                <div class="flex flex-col gap-2 items-center">
                    <label class="text-sm" for="theme.color">
                        {{ $t('forms.prefs_calendar_view') }}
                    </label>
                    <SelectButton v-model="customerInfo.calendar.view" :options="calendarView" optionLabel="value" optionValue="value" dataKey="value" aria-labelledby="custom">
                        <template #option="slotProps">
                            <i :class="slotProps.option.icon"></i> {{slotProps.option.label}}
                        </template>
                    </SelectButton>
                </div>
                <div class="border-l dark:border-gray-800"></div>
                <div class="flex flex-col gap-2 items-center">
                    <label class="text-sm" for="theme.color">
                        {{ $t('forms.theme_week_numbers') }}
                    </label>
                    <ToggleButton v-model="customerInfo.calendar.showWeekNumbers" :on-label="$t('common.yes')" :off-label="$t('common.no')"></ToggleButton>
                </div>
            </div>
        </div>
        <div class="flex justify-end mt-4">
            <Button :label="$t('common.continue')" @click="emit('done', customerInfo)"></Button>
        </div>
    </div>
</template>

<style scoped lang="scss">

</style>
